import { z } from 'zod';

import { isObject, toString } from '../../utils/common.util';
import { extractByPath } from '../../utils/json.util';
import * as Blocks from '../blocks';
import { InvalidComponentError } from './base.component';

export const ComponentLiteral =
  Blocks.PropertyBlock.ComponentPropertyBlock.CustomFileUploadLiteral;

export const FileUploadComponentSchema = z.object({
  name: Blocks.PropertyBlock.NamePropertyBlock.NameBlockSchema,
  component: ComponentLiteral,
  params: z.object({
    validations: z
      .object({
        maxSizeMB:
          Blocks.ValidationBlock.MaxValueValidation.MaxValueValidationBlockSchema.optional(),
        allowedFileFormats:
          Blocks.ValidationBlock.AllowedListValidation.AllowedListValidationBlockSchema.optional(),
        required:
          Blocks.ValidationBlock.RequiredValidation.RequireBlockSchema.optional()
      })
      .optional(),
    labels: Blocks.PropertyBlock.LabelPropertyBlock.LabelBlockSchema.optional(),
    caseDataField:
      Blocks.PropertyBlock.CaseDataPropertyBlock.CaseDataFieldSchema,
    actions: z.array(
      Blocks.PropertyBlock.ActionPropertyBlock.ActionPropertyBlockSchema
    )
  })
});
export type FileUploadComponent = z.infer<typeof FileUploadComponentSchema>;

export const FileUploadComponentWithValueSchema =
  FileUploadComponentSchema.merge(
    z.object({
      value: z.string().optional()
    })
  );
export type FileUploadComponentWithValue = z.infer<
  typeof FileUploadComponentWithValueSchema
>;

type isValidStructure = (
  structure: unknown
) => structure is FileUploadComponent;
export const isValidStructure: isValidStructure = (
  structure: unknown
): structure is FileUploadComponent =>
  FileUploadComponentSchema.safeParse(structure).success;

type ObjectAsValue = {
  value: unknown;
  sizeMB?: number;
  format?: string;
};
type SingleValue = unknown;

function isInputValueObject(value: unknown): value is ObjectAsValue {
  return isObject(value);
}

export function isValidateData(
  component: FileUploadComponent,
  inputValue: unknown
): boolean;
export function isValidateData(
  component: FileUploadComponent,
  inputValue: ObjectAsValue
): boolean;
export function isValidateData(
  component: FileUploadComponent,
  inputValue: unknown
): boolean;
export function isValidateData(
  component: FileUploadComponent,
  inputValue: SingleValue | ObjectAsValue | unknown
): boolean {
  if (!component.params.validations) {
    return true;
  }

  const { value, sizeMB, format } = isInputValueObject(inputValue)
    ? inputValue
    : { value: inputValue, sizeMB: undefined, format: undefined };

  const { maxSizeMB, allowedFileFormats, required } =
    component.params.validations;
  if (required !== undefined) {
    if (
      Blocks.ValidationBlock.RequiredValidation.isRequired(value, required) ===
      false
    ) {
      return false;
    }
  }

  if (maxSizeMB !== undefined) {
    if (
      Blocks.ValidationBlock.MaxValueValidation.isMaxValue(
        sizeMB,
        maxSizeMB
      ) === false
    ) {
      return false;
    }
  }

  if (allowedFileFormats !== undefined) {
    if (
      Blocks.ValidationBlock.AllowedListValidation.isAllowedList(
        format,
        allowedFileFormats
      ) === false
    ) {
      return false;
    }
  }

  return true;
}

type PopulateFromCaseDataField = (
  field: FileUploadComponent,
  inputJSON: Record<string, unknown>
) => FileUploadComponentWithValue;
export const populateFromCaseDataField: PopulateFromCaseDataField = (
  field,
  inputJSON
) => {
  return {
    ...field,
    value: toString(
      extractByPath({ json: inputJSON, path: field.params.caseDataField })
    )
  };
};

export type BuildParams = Omit<FileUploadComponent, 'component'>;
type Build = (
  params: BuildParams
) => FileUploadComponent | InvalidComponentError;
export const build: Build = params => {
  const component = {
    ...params,
    component: ComponentLiteral.value
  };

  const parsedResult = FileUploadComponentSchema.safeParse(component);
  if (parsedResult.success) {
    return parsedResult.data;
  }

  return new InvalidComponentError(
    ComponentLiteral.value,
    parsedResult.error,
    params
  );
};
